import { useSearchParams } from 'react-router-dom';
import React, { useCallback, useMemo } from 'react';
import { BoxProps, IconButton, Tooltip, Typography } from '@mui/material';
import { Add } from '@mui/icons-material';
import { IModalEntity } from '../../../../interfaces/modalEntity';
import { IListItem } from '../../../../interfaces/listItem';
import { ENTITY_QUERY_PARAM } from '../../../../core/variables';
import { ColumnBoxFlexFullWidth, FlexBoxFullWidth } from '../../../common/boxes';
import { ShadowContainer } from '../../../common/ShadowContainer';

export interface IProfileListProps<T> extends BoxProps {
  list: Array<T>,
  title: string;
  emptyListText: string;
  entityQueryParam?: string;
  ListItemComponent: React.FC<IListItem<T>>;
  EntityComponent: React.FC<IModalEntity<T>>
}

const ProfileList = <T extends unknown>({
                                          list,
                                          title,
                                          ListItemComponent,
                                          EntityComponent,
                                          emptyListText,
                                          entityQueryParam = ENTITY_QUERY_PARAM,
                                          ...props
                                        }: IProfileListProps<T>) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const entityQueryId = useMemo<string | null>(() => searchParams.get(entityQueryParam),
    [searchParams, entityQueryParam]);

  const entity = useMemo(() => list.find(e => (e as any).id === entityQueryId),
    [list, entityQueryId]);

  const closeModal = useCallback(() => {
    searchParams.delete(entityQueryParam);
    setSearchParams(searchParams);
  }, [searchParams, setSearchParams, entityQueryParam]);

  const openModal = useCallback((id: 'new' | number) => {
    searchParams.set(entityQueryParam, id.toString());
    setSearchParams(searchParams);
  }, [searchParams, setSearchParams, entityQueryParam]);

  return (
    <ShadowContainer maxWidth={800} alignSelf={'center'} {...props}>

      <FlexBoxFullWidth justifyContent={'space-between'} mb={3}>
        <Typography variant={'h3'}>
          {title}
        </Typography>
        <Tooltip title={'Add'}>
          <IconButton color={'primary'} onClick={() => openModal('new')}>
            <Add/>
          </IconButton>
        </Tooltip>
      </FlexBoxFullWidth>

      <ColumnBoxFlexFullWidth>
        {
          list.map((ent, i) => (
            <ListItemComponent
              key={(ent as any).id.toString() + i}
              queryParam={entityQueryParam}
              entity={ent}
            />
          ))
        }
      </ColumnBoxFlexFullWidth>

      {
        !list.length &&
        <Typography>
          {emptyListText}
        </Typography>
      }
      
      {
        (entityQueryId === 'new' || entity) &&
        <EntityComponent onClose={closeModal} entity={entity}/>
      }
    </ShadowContainer>
  );
};

export default ProfileList;